<template>
  <div class="hierarchical-select">
    <!-- Múltiple: ítems ya elegidos -->
    <div
      v-if="multiple && normalizedSelectedArray.length"
      class="selected-multiple-chips mb-2"
    >
      <span
        v-for="(it, idx) in normalizedSelectedArray"
        :key="'hsel-' + idx + '-' + (it && it._id != null ? it._id : idx)"
        class="badge badge-primary mr-1 mb-1 d-inline-flex align-items-center"
      >
        {{ getItemHierarchyLabel(it) }}
        <button
          v-if="!disabled"
          type="button"
          class="btn btn-link btn-sm p-0 ml-1 text-white"
          style="line-height: 1; font-size: 14px"
          title="Quitar"
          @click="removeSelectedAt(idx)"
        >
          <i class="fas fa-times"></i>
        </button>
      </span>
    </div>

    <!-- Selectores en cascada dinámicos -->
    <div class="selectors-row">
      <!-- Selector nivel 0 (raíz) -->
      <div 
        v-for="(level, index) in selectorLevels" 
        :key="index" 
        class="level-selector"
      >
        <multiselect
          :value="getSelectedObjectForLevel(index)"
          :options="getOptionsForLevel(index)"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :preselect-first="false"
          :allow-empty="true"
          :searchable="true"
          :show-labels="true"
          :taggable="true"
          :create-tag="true"
          label="label"
          track-by="_id"
          :disabled="disabled || (index > 0 && !getSelectedIdForLevel(index - 1))"
          :placeholder="getPlaceholderForLevel(index)"
          :data-level="index"
          class="level-multiselect"
          @input="(val) => onLevelChange(index, val)"
          @tag="(tag) => onTagAdded(index, tag)"
        >
          <template #option="props">
            <span>{{ props.option.label }}</span>
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Múltiple: añadir la ruta actual al array (sin pisar otras elecciones) -->
    <div
      v-if="multiple"
      class="add-selection-row d-flex flex-wrap align-items-center mb-2"
    >
      <button
        type="button"
        class="btn btn-sm btn-outline-primary mr-2 mb-1"
        :disabled="disabled || !pendingSelectionObject || isPendingDuplicate"
        @click="addPendingToSelection"
      >
        Añadir a la selección
      </button>
      <button
        v-if="normalizedSelectedArray.length && !disabled"
        type="button"
        class="btn btn-sm btn-outline-secondary mb-1"
        @click="clearSelection"
      >
        Limpiar todo
      </button>
    </div>

    <!-- Mostrar valor seleccionado (opcional, más compacto) -->
    <div v-if="!multiple && displayValue" class="selected-value-compact">
      <span class="selected-text">{{ displayValue }}</span>
      <button
        v-if="!disabled"
        type="button"
        class="btn btn-sm btn-outline-secondary clear-btn"
        @click="clearSelection"
        title="Limpiar selección"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  name: "HierarchicalSelect",
  components: {
    Multiselect,
  },
  props: {
    value: {
      type: [String, Object, Array],
      default: null,
    },
    listOptions: {
      type: Array,
      required: true,
    },
    parentLabel: {
      type: String,
      default: "Categoría",
    },
    childLabel: {
      type: String,
      default: "Subcategoría",
    },
    parentPlaceholder: {
      type: String,
      default: "",
    },
    childPlaceholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    listType: {
      type: String,
      default: null,
    },
    /**
     * Placeholders opcionales por índice de nivel (0-based), para jerarquías de
     * más de 2 niveles donde el genérico "Selecciona nivel N" no es claro para
     * el usuario. Si no se define un índice, se usa el fallback existente.
     */
    levelPlaceholders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedLevels: [], // Array dinámico de selecciones por nivel (almacena IDs)
      localListOptions: [], // Copia local de las opciones para poder agregar nuevas
    };
  },
  created() {
    // Inicializar localListOptions con las opciones iniciales
    if (this.listOptions && this.listOptions.length > 0) {
      this.localListOptions = [...this.listOptions];
    }
  },
  computed: {
    // Calcular cuántos niveles de selectores necesitamos mostrar
    selectorLevels() {
      // Calcular la profundidad máxima de la jerarquía
      const maxDepth = this.calculateMaxDepth(this.localListOptions);
      // Siempre mostrar al menos 1 selector (nivel raíz)
      // Mostrar hasta maxDepth + 1 selectores (raíz + todos los niveles hijos)
      return Math.max(1, maxDepth + 1);
    },
    displayValue() {
      const labels = [];
      for (let i = 0; i < this.selectedLevels.length; i++) {
        if (this.selectedLevels[i]) {
          const item = this.localListOptions.find(
            (opt) =>
              opt._id === this.selectedLevels[i] ||
              String(opt._id) === String(this.selectedLevels[i])
          );
          if (item) {
            labels.push(item.label);
          }
        }
      }
      return labels.length > 0 ? labels.join(" > ") : null;
    },
    normalizedSelectedArray() {
      if (!this.multiple) return [];
      const raw = this.value;
      const arr = Array.isArray(raw)
        ? raw
        : raw != null && raw !== ""
          ? [raw]
          : [];
      return arr.map((v) => this.resolveToOptionObject(v)).filter(Boolean);
    },
    pendingSelectionObject() {
      return this.getDeepestSelectedOption();
    },
    isPendingDuplicate() {
      const p = this.pendingSelectionObject;
      if (!p || p._id == null) return false;
      return this.normalizedSelectedArray.some(
        (x) => String(x._id) === String(p._id)
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.initializeFromValue(newValue);
      },
    },
    listOptions: {
      immediate: true,
      deep: true,
      handler(newOptions) {
        // Mantener una copia local de las opciones
        this.localListOptions = [...(newOptions || [])];
        // Re-inicializar si cambian las opciones
        this.$nextTick(() => {
          this.initializeFromValue(this.value);
        });
      },
    },
  },
  methods: {
    // Calcular la profundidad máxima de la jerarquía
    calculateMaxDepth(options) {
      if (!options || options.length === 0) return 0;

      // Construir un mapa de hijos por padre
      const childrenMap = new Map();
      const roots = [];

      options.forEach((item) => {
        if (!item.parent) {
          roots.push(item);
        } else {
          if (!childrenMap.has(item.parent)) {
            childrenMap.set(item.parent, []);
          }
          childrenMap.get(item.parent).push(item);
        }
      });

      // Función recursiva para calcular profundidad
      const getDepth = (itemId, visited = new Set()) => {
        if (visited.has(itemId)) return 0; // Evitar ciclos
        visited.add(itemId);

        const children = childrenMap.get(itemId) || [];
        if (children.length === 0) return 0;

        let maxChildDepth = 0;
        children.forEach((child) => {
          const childDepth = getDepth(child._id, visited);
          maxChildDepth = Math.max(maxChildDepth, childDepth);
        });

        return maxChildDepth + 1;
      };

      let maxDepth = 0;
      roots.forEach((root) => {
        const depth = getDepth(root._id);
        maxDepth = Math.max(maxDepth, depth);
      });

      return maxDepth;
    },
    // Obtener opciones para un nivel específico
    getOptionsForLevel(levelIndex) {
      if (levelIndex === 0) {
        // Nivel 0: elementos raíz (sin padre)
        const rootOptions = this.localListOptions.filter((item) => {
          if (!item) return false;
          // Verificar que no tiene parent (o parent es null/undefined/empty string)
          // También manejar el caso donde parent es un objeto (extraer el _id)
          const parent = item.parent;
          if (!parent) return true;
          if (typeof parent === 'object' && parent._id) {
            return false; // Tiene parent como objeto
          }
          return parent === null || parent === undefined || parent === '';
        });
        return rootOptions;
      } else {
        // Niveles superiores: hijos del elemento seleccionado en el nivel anterior
        const parentId = this.getSelectedIdForLevel(levelIndex - 1);
        if (!parentId) {
          return [];
        }
        const childOptions = this.localListOptions.filter(
          (item) => {
            if (!item || !item.parent) return false;
            // Manejar parent como string/ID o como objeto
            const itemParent = item.parent;
            if (typeof itemParent === 'object' && itemParent._id) {
              // Si parent es un objeto, comparar el _id
              return itemParent._id === parentId || String(itemParent._id) === String(parentId);
            }
            // Si parent es string/ID, comparar directamente
            return itemParent === parentId || String(itemParent) === String(parentId);
          }
        );
        return childOptions;
      }
    },
    // Obtener el ID seleccionado para un nivel (helper)
    getSelectedIdForLevel(levelIndex) {
      return this.selectedLevels[levelIndex] || null;
    },
    // Obtener el objeto completo seleccionado para un nivel (para multiselect)
    getSelectedObjectForLevel(levelIndex) {
      const selectedId = this.selectedLevels[levelIndex];
      if (!selectedId) return null;
      return (
        this.localListOptions.find(
          (opt) =>
            opt._id === selectedId || String(opt._id) === String(selectedId)
        ) || null
      );
    },
    // Obtener placeholder para un nivel específico
    getPlaceholderForLevel(levelIndex) {
      if (this.levelPlaceholders && this.levelPlaceholders[levelIndex]) {
        return this.levelPlaceholders[levelIndex];
      }
      if (levelIndex === 0) {
        return this.parentPlaceholder || "Selecciona categoría";
      } else if (levelIndex === 1) {
        return this.childPlaceholder || "Selecciona subcategoría";
      } else {
        return `Selecciona nivel ${levelIndex + 1}`;
      }
    },
    // Manejar cambio en cualquier nivel
    onLevelChange(changedLevelIndex, value) {
      // Si el valor es null, limpiar la selección
      if (!value) {
        this.$set(this.selectedLevels, changedLevelIndex, null);
        // Limpiar selecciones de niveles superiores
        for (let i = changedLevelIndex + 1; i < this.selectedLevels.length; i++) {
          this.$set(this.selectedLevels, i, null);
        }
        if (!this.multiple) {
          this.emitValue();
        }
        return;
      }

      // Si el valor es un objeto, usar su _id; si es string, usarlo directamente
      const selectedId = value && typeof value === "object" ? value._id : value;
      
      // Actualizar el nivel seleccionado
      this.$set(this.selectedLevels, changedLevelIndex, selectedId);
      
      // Limpiar selecciones de niveles superiores cuando cambia un nivel anterior
      for (let i = changedLevelIndex + 1; i < this.selectedLevels.length; i++) {
        this.$set(this.selectedLevels, i, null);
      }
      // Ajustar el tamaño del array si es necesario
      while (
        this.selectedLevels.length > changedLevelIndex + 1 &&
        !this.selectedLevels[this.selectedLevels.length - 1]
      ) {
        this.selectedLevels.pop();
      }
      if (!this.multiple) {
        this.emitValue();
      }
    },
    // Manejar creación de nuevo término
    async onTagAdded(levelIndex, tag) {
      // Validar que el tag no esté vacío
      if (!tag || !tag.trim()) {
        return;
      }

      const tagLabel = tag.trim();

      // Validar que tenemos listType
      if (!this.listType) {
        console.error('No se puede crear término sin listType');
        return;
      }

      // Determinar el parent según el nivel
      let parentId = null;
      if (levelIndex > 0) {
        parentId = this.getSelectedIdForLevel(levelIndex - 1);
        // Si es un nivel superior y no hay parent seleccionado, no se puede agregar
        if (!parentId) {
          console.warn(`No se puede agregar término en nivel ${levelIndex} sin seleccionar un parent en el nivel ${levelIndex - 1}`);
          return;
        }
      }

      try {
        // Llamar al endpoint del backend para crear el término
        const { wencesApi } = await import('@/api/wencesApi');
        const response = await wencesApi.post(`api/gotrading/lists/${this.listType}/values`, {
          label: tagLabel,
          parent: parentId,
          isChild: levelIndex > 0,
        });

        // El backend devuelve el término creado con su _id real
        const createdTerm = response.data;

        // Agregar a las opciones locales
        this.localListOptions.push(createdTerm);
        // Forzar actualización de la lista para que Vue detecte el cambio
        this.$set(this.localListOptions, this.localListOptions.length - 1, createdTerm);

        // Seleccionar el nuevo tag con el _id real del backend
        this.$set(this.selectedLevels, levelIndex, createdTerm._id);

        // Limpiar selecciones de niveles superiores
        for (let i = levelIndex + 1; i < this.selectedLevels.length; i++) {
          this.$set(this.selectedLevels, i, null);
        }

        // Emitir el valor después de que Vue haya actualizado el DOM
        this.$nextTick(() => {
          if (this.multiple) {
            this.addPendingToSelection();
          } else {
            this.emitValue();
          }
        });
      } catch (error) {
        console.error('Error al crear término en el backend:', error);
        // Mostrar mensaje de error al usuario
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.error || 'No se pudo crear el término',
        });
      }
    },
    initializeFromValue(value) {
      this.selectedLevels = [];

      if (this.multiple) {
        this.$set(this.selectedLevels, 0, null);
        return;
      }

      if (!value) {
        this.$set(this.selectedLevels, 0, null);
        return;
      }

      let item = null;

      if (typeof value === "object" && value._id) {
        item = this.localListOptions.find(
          (opt) =>
            opt._id === value._id || String(opt._id) === String(value._id)
        );
      } else if (typeof value === "string") {
        item = this.localListOptions.find(
          (opt) => opt._id === value || String(opt._id) === String(value)
        );
      }

      if (item) {
        const ancestors = this.getAncestors(item);
        ancestors.forEach((ancestor, index) => {
          this.$set(this.selectedLevels, index, ancestor._id);
        });
      } else {
        this.$set(this.selectedLevels, 0, null);
      }
    },
    // Obtener todos los ancestros de un elemento (desde la raíz hasta el elemento)
    getAncestors(item) {
      const ancestors = [];
      let current = item;

      // Recorrer hacia arriba desde el elemento hasta la raíz
      while (current) {
        ancestors.unshift(current); // Agregar al inicio para mantener orden raíz -> hijo

        if (current.parent) {
          const pid =
            typeof current.parent === "object" && current.parent._id != null
              ? current.parent._id
              : current.parent;
          current = this.localListOptions.find(
            (opt) => opt._id === pid || String(opt._id) === String(pid)
          );
        } else {
          current = null;
        }
      }

      return ancestors;
    },
    emitValue() {
      this.emitSingleValue();
    },
    getDeepestSelectedOption() {
      for (let i = this.selectedLevels.length - 1; i >= 0; i--) {
        if (this.selectedLevels[i]) {
          return (
            this.localListOptions.find(
              (item) =>
                item._id === this.selectedLevels[i] ||
                String(item._id) === String(this.selectedLevels[i])
            ) || null
          );
        }
      }
      return null;
    },
    emitSingleValue() {
      this.$emit("input", this.getDeepestSelectedOption());
    },
    resolveToOptionObject(v) {
      if (v == null || v === "") return null;
      if (typeof v === "object" && v._id != null) {
        const found = this.localListOptions.find(
          (o) => o._id === v._id || String(o._id) === String(v._id)
        );
        return found || v;
      }
      const id = typeof v === "object" ? v._id || v.id : v;
      if (id == null || id === "") return null;
      return (
        this.localListOptions.find(
          (o) => o._id === id || String(o._id) === String(id)
        ) || { _id: id, label: String(id) }
      );
    },
    getItemHierarchyLabel(item) {
      if (!item) return "";
      const ancestors = this.getAncestors(item);
      return ancestors.map((a) => a.label).join(" > ");
    },
    addPendingToSelection() {
      const obj = this.getDeepestSelectedOption();
      if (!obj || obj._id == null) return;
      const current = this.normalizedSelectedArray.slice();
      if (current.some((x) => String(x._id) === String(obj._id))) {
        return;
      }
      this.$emit("input", [...current, obj]);
      this.selectedLevels = [];
      this.$set(this.selectedLevels, 0, null);
    },
    removeSelectedAt(index) {
      const arr = this.normalizedSelectedArray.slice();
      arr.splice(index, 1);
      this.$emit("input", arr);
    },
    clearSelection() {
      this.selectedLevels = [null];
      this.$emit("input", this.multiple ? [] : null);
    },
  },
};
</script>

<style scoped>
.hierarchical-select {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selectors-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex-wrap: wrap;
}

.level-selector {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 150px;
}

.level-multiselect {
  width: 100%;
}

/* Estilos para multiselect en cada nivel */
:deep(.level-multiselect .multiselect) {
  min-height: calc(1.5em + 0.75rem + 2px);
}

:deep(.level-multiselect .multiselect__tags) {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

:deep(.level-multiselect .multiselect__tags:focus-within) {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

:deep(.level-multiselect .multiselect--disabled .multiselect__tags) {
  background-color: #e9ecef;
  opacity: 1;
  color: #6c757d;
  cursor: not-allowed;
}

/* Indicador visual para niveles anidados */
.level-multiselect[data-level="0"] :deep(.multiselect__tags) {
  font-weight: 500;
}

.level-multiselect[data-level="1"] :deep(.multiselect__tags) {
  padding-left: 1.5rem;
}

.level-multiselect[data-level="2"] :deep(.multiselect__tags) {
  padding-left: 2.5rem;
}

.level-multiselect[data-level="3"] :deep(.multiselect__tags) {
  padding-left: 3.5rem;
}

/* Asegurar que el dropdown se muestre correctamente */
:deep(.level-multiselect .multiselect__content-wrapper) {
  z-index: 99999;
  position: absolute;
}

.selected-value-compact {
  background: #e8f5e8;
  border: 1px solid #4caf50;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.selected-text {
  flex: 1;
  color: #1b5e20;
  font-weight: 500;
}

.clear-btn {
  padding: 2px 6px;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
  border: 1px solid #6c757d;
  background-color: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.clear-btn:hover {
  background-color: #6c757d;
  color: #fff;
}

/* Responsive: en pantallas pequeñas, apilar verticalmente */
@media (max-width: 576px) {
  .selectors-row {
    flex-direction: column;
    gap: 4px;
  }

  .level-selector {
    min-width: 100%;
  }
}
</style>